import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UsersApi } from '@/api/users';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { devLog } from '@/lib/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: string | null;
  approved: boolean | null;
  tenantId: string | null;
  mfaVerified: boolean;
  mfaRequired: boolean;
  mfaEnrolled: boolean;
  mustChangePassword: boolean;
  subscriptionStatus: string | null;
  trialDaysLeft: number | null;
  refreshMfa: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  userRole: null,
  approved: null,
  tenantId: null,
  mfaVerified: false,
  mfaRequired: false,
  mfaEnrolled: false,
  mustChangePassword: false,
  subscriptionStatus: null,
  trialDaysLeft: null,
  refreshMfa: async () => {},
  refreshProfile: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [approved, setApproved] = useState<boolean | null>(null);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantForceMfa, setTenantForceMfa] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  /** Utilisateur dont le profil est chargé ou en cours : un rafraîchissement de session n'oblige pas à tout recharger. */
  const profileUserIdRef = useRef<string | null>(null);

  const isDevEnvironment =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.startsWith('id-preview--'));

  const mustChangePassword = !!user?.user_metadata?.must_change_password;

  // MFA désactivé — remplacé par activation licence machine (device_licenses)
  // Ancien code: const mfaRequired = !isDevEnvironment && (userRole === 'super_admin' || userRole === 'admin' || tenantForceMfa);
  const mfaRequired = false;

  const fetchRoleAndApproval = useCallback(async (userId: string) => {
    const [roleRes, profileRes] = await Promise.all([
      UsersApi.getUserRole(userId),
      supabase
        .from('profiles')
        .select('approved, tenant_id, subscription_status, trial_ends_at')
        .eq('user_id', userId)
        .maybeSingle(),
    ]);
    setUserRole((roleRes.ok ? roleRes.value : null) ?? 'user');
    setApproved(profileRes.data?.approved ?? false);
    setTenantId(profileRes.data?.tenant_id ?? null);

    // Sentry scope : tag tenant_id + user pour filtrer les issues/replays
    // par tenant dans le dashboard et pour le beforeErrorSampling exclude.
    setSentryUser({
      id: userId,
      email: user?.email,
      tenantId: profileRes.data?.tenant_id ?? undefined,
    });

    // --- Subscription status: TENANT is the source of truth, profile is a cache ---
    let effectiveStatus = profileRes.data?.subscription_status ?? 'trial';
    let effectiveTrialEndsAt = profileRes.data?.trial_ends_at;

    // If user belongs to a tenant, check the TENANT subscription_status (overrides profile)
    if (profileRes.data?.tenant_id) {
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('subscription_status, trial_ends_at, force_mfa')
        .eq('id', profileRes.data.tenant_id)
        .maybeSingle();

      if (tenantData) {
        const tenantStatus = tenantData.subscription_status ?? 'trial';
        // Tenant active/enterprise/etc. → user is active (never auto-suspend)
        if (tenantStatus === 'active') {
          effectiveStatus = 'active';
          effectiveTrialEndsAt = null;
          // Sync profile if it's out of date
          if (profileRes.data?.subscription_status !== 'active') {
            await supabase
              .from('profiles')
              .update({
                subscription_status: 'active',
                trial_ends_at: null,
              } as any)
              .eq('user_id', userId);
          }
        } else if (tenantStatus === 'trial') {
          effectiveTrialEndsAt = tenantData.trial_ends_at || effectiveTrialEndsAt;
        }
        setTenantForceMfa(tenantData.force_mfa ?? false);
      }
    }

    setSubscriptionStatus(effectiveStatus);

    if (effectiveStatus === 'trial' && effectiveTrialEndsAt) {
      const diff = Math.ceil(
        (new Date(effectiveTrialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      setTrialDaysLeft(Math.max(0, diff));
      if (diff <= 0) {
        setSubscriptionStatus('suspended');
        await supabase
          .from('profiles')
          .update({ subscription_status: 'suspended' } as any)
          .eq('user_id', userId);
      }
    } else {
      setTrialDaysLeft(null);
    }

    // Fetch force_mfa from tenant if not already fetched above
    if (profileRes.data?.tenant_id && !effectiveStatus) {
      const { data: tenantRows } = await supabase.rpc('get_my_tenant_info');
      setTenantForceMfa(tenantRows?.[0]?.force_mfa ?? false);
    }

    setProfileLoaded(true);
  }, []);

  // MFA monthly persistence — only require 2FA once per 30 days per user
  const MFA_STORAGE_KEY = 'visionpos-mfa-verified';
  const MFA_VALIDITY_DAYS = 30;

  const isMfaRecentlyVerified = useCallback((userId: string): boolean => {
    try {
      const raw = localStorage.getItem(MFA_STORAGE_KEY);
      if (!raw) return false;
      const stored = JSON.parse(raw);
      if (stored.userId !== userId) return false;
      const elapsed = Date.now() - stored.verifiedAt;
      return elapsed < MFA_VALIDITY_DAYS * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }, []);

  const saveMfaVerification = useCallback((userId: string) => {
    try {
      localStorage.setItem(
        MFA_STORAGE_KEY,
        JSON.stringify({
          userId,
          verifiedAt: Date.now(),
        }),
      );
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const checkMfaStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) {
        setMfaEnrolled(false);
        setMfaVerified(false);
        return;
      }
      const verifiedFactors = data.totp.filter((f) => f.status === 'verified');
      setMfaEnrolled(verifiedFactors.length > 0);

      // Check if MFA was verified in the last 30 days (skip re-auth)
      const currentUser = (await supabase.auth.getUser()).data.user;
      if (currentUser && verifiedFactors.length > 0 && isMfaRecentlyVerified(currentUser.id)) {
        setMfaVerified(true);
        return;
      }

      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData) {
        const verified = aalData.currentLevel === 'aal2';
        setMfaVerified(verified);
        // Persist successful MFA verification for 30 days
        if (verified && currentUser) {
          saveMfaVerification(currentUser.id);
        }
      }
    } catch {
      setMfaEnrolled(false);
      setMfaVerified(false);
    }
  }, [isMfaRecentlyVerified, saveMfaVerification]);

  const refreshMfa = useCallback(async () => {
    await checkMfaStatus();
    // After MFA challenge, also persist if now verified
    const currentUser = (await supabase.auth.getUser()).data.user;
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalData?.currentLevel === 'aal2' && currentUser) {
      saveMfaVerification(currentUser.id);
      setMfaVerified(true);
    }
  }, [checkMfaStatus, saveMfaVerification]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchRoleAndApproval(user.id);
    }
  }, [user, fetchRoleAndApproval]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Jeton rafraîchi ou retour sur l'onglet : même utilisateur, la page en cours reste montée.
        // Remettre « profil non chargé » démonterait toute la page, caisse et paiement compris.
        if (profileUserIdRef.current !== session.user.id) {
          profileUserIdRef.current = session.user.id;
          setProfileLoaded(false);
        }
        setLoading(false);
        setTimeout(() => fetchRoleAndApproval(session.user.id), 0);
        setTimeout(() => checkMfaStatus(), 0);

        // NF525 JET: log LOGIN event (fire-and-forget, don't block auth flow).
        // Must include tenant_id — fiscal_events RLS rejects inserts where
        // tenant_id is NULL for non-super-admin users (OR clause in policy).
        if (_event === 'SIGNED_IN') {
          Promise.all([
            UsersApi.getUserRole(session.user.id),
            supabase
              .from('profiles')
              .select('tenant_id')
              .eq('user_id', session.user.id)
              .maybeSingle(),
          ])
            .then(([roleRes, { data: prof }]) => {
              const role = roleRes.ok ? roleRes.value : null;
              supabase
                .from('fiscal_events')
                .insert({
                  event_type: 'LOGIN',
                  user_id: session.user.id,
                  tenant_id: prof?.tenant_id ?? null,
                  details: { user_email: session.user.email, role: role ?? 'user' },
                } as any)
                // .then(success, failure) — .catch() n'est pas sur PromiseLike
                .then(
                  () => {},
                  (e) => console.error('[NF525] Failed to log LOGIN:', e),
                );
            })
            .catch(() => {});
        }
      } else {
        profileUserIdRef.current = null;
        setUserRole(null);
        setApproved(null);
        setTenantId(null);
        setMfaVerified(false);
        setMfaEnrolled(false);
        setSubscriptionStatus(null);
        setTrialDaysLeft(null);
        setProfileLoaded(true);
        setLoading(false);
      }
    });

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          if (profileUserIdRef.current !== session.user.id) {
            profileUserIdRef.current = session.user.id;
            setProfileLoaded(false);
          }
          setLoading(false);
          fetchRoleAndApproval(session.user.id);
          checkMfaStatus();
        } else {
          setProfileLoaded(true);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[Auth] getSession failed:', err);
        setLoading(false);
        setProfileLoaded(true);
      });

    // Proactive JWT refresh every 45 min (JWT expires at 60 min)
    // Critical for kiosk/POS devices that stay logged in for hours
    const refreshInterval = setInterval(
      async () => {
        try {
          const { data } = await supabase.auth.getSession();
          if (data?.session) {
            const { error } = await supabase.auth.refreshSession();
            if (error) console.warn('[Auth] JWT refresh failed:', error.message);
            else devLog('[Auth] JWT refreshed proactively');
          }
        } catch (e) {
          console.warn('[Auth] JWT refresh error:', e);
        }
      },
      45 * 60 * 1000,
    ); // 45 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [checkMfaStatus, fetchRoleAndApproval]);

  const signOut = async () => {
    // Note: don't clear MFA storage on sign-out — the 30-day window persists
    // so the user won't need to re-enter 2FA when logging back in within the month

    // CRITIQUE NF525 multi-tenant: purge React Query cache avant signOut
    // Empêche qu'un utilisateur A déconnecté laisse ses données en cache pour
    // un utilisateur B (autre tenant) qui se connecte ensuite sur la même machine.
    try {
      queryClient.clear();
    } catch {
      /* ignore */
    }
    clearSentryUser();
    // Cet appareil seulement : sans portée, Supabase ferme TOUTES les sessions du compte
    // (toutes les caisses et le back-office du commerce étaient déconnectés).
    await supabase.auth.signOut({ scope: 'local' });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading: loading || (!!user && !profileLoaded),
        userRole,
        approved,
        tenantId,
        mfaVerified,
        mfaRequired,
        mfaEnrolled,
        mustChangePassword,
        subscriptionStatus,
        trialDaysLeft,
        refreshMfa,
        refreshProfile,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
